// src/utils/promotionModel.js

// 🎉 Promotion Types
export const PROMOTION_TYPES = {
  PRODUCT_BASED: 'product_based',
  ORDER_BASED: 'order_based',
  // CUSTOMER_BASED: 'customer_based',
  // QUANTITY_BASED: 'quantity_based'
}

// 💰 Discount Types
export const DISCOUNT_TYPES = {
  FIXED: 'fixed',
  PERCENTAGE: 'percentage'
}

// 🔢 Priority Levels
export const PRIORITY_LEVELS = {
  HIGHEST: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4
}

// ⏰ Condition Types
export const CONDITION_TYPES = {
  DAYS_ACTIVE: 'days_active',
  TIME_RANGE: 'time_range',
  MIN_QUANTITY: 'min_quantity',
  MAX_USES_PER_ORDER: 'max_uses_per_order',
  MIN_ORDER_VALUE: 'min_order_value',
  PAYMENT_METHOD: 'payment_method',
  FIRST_ORDER: 'first_order'
}

// 🎨 Type Configuration
export const PROMOTION_TYPE_CONFIG = {
  [PROMOTION_TYPES.PRODUCT_BASED]: {
    label: 'Product Based',
    value: 'product_based',
    icon: '🛍️',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    description: 'Discounts on specific products'
  },
  [PROMOTION_TYPES.ORDER_BASED]: {
    label: 'Order Based',
    value: 'order_based',
    icon: '🛒',
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    description: 'Discounts based on order value'
  }
}

// 🎨 Priority Configuration
export const PRIORITY_CONFIG = {
  [PRIORITY_LEVELS.HIGHEST]: {
    label: 'Highest',
    value: 1,
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800'
  },
  [PRIORITY_LEVELS.HIGH]: {
    label: 'High',
    value: 2,
    color: 'orange',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800'
  },
  [PRIORITY_LEVELS.MEDIUM]: {
    label: 'Medium',
    value: 3,
    color: 'yellow',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800'
  },
  [PRIORITY_LEVELS.LOW]: {
    label: 'Low',
    value: 4,
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800'
  }
}

// 📋 Promotion Schema/Interface
export const PromotionSchema = {
  _id: '',
  name: '',
  description: '',
  promotionType: '',
  discountType: '',
  discountValue: 0,
  priority: 1,
  targetAudience: '', // Customer Group ID
  startDate: null,
  endDate: null,
  products: [],
  conditions: [],
  isActive: true,
  createdBy: null,
  updatedBy: null,
  createdAt: null,
  updatedAt: null,
  // Populated fields (for display)
  targetAudienceDetails: null,
  productsDetails: []
}

// ✅ Validation Rules
export const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 3,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s\-&'()%.]+$/,
    message: 'Name must be 3-100 characters and contain only letters, numbers, spaces, and common symbols'
  },
  description: {
    required: false,
    maxLength: 500,
    message: 'Description cannot exceed 500 characters'
  },
  promotionType: {
    required: true,
    enum: Object.values(PROMOTION_TYPES),
    message: 'Please select a valid promotion type'
  },
  discountType: {
    required: true,
    enum: Object.values(DISCOUNT_TYPES),
    message: 'Please select a valid discount type'
  },
  discountValue: {
    required: true,
    min: 0.01,
    max: 999999.99,
    message: 'Discount value must be between 0.01 and 999,999.99'
  },
  priority: {
    required: true,
    enum: Object.values(PRIORITY_LEVELS),
    message: 'Please select a valid priority'
  },
  targetAudience: {
    required: true,
    message: 'Please select a target audience'
  },
  startDate: {
    required: true,
    message: 'Start date is required'
  },
  endDate: {
    required: true,
    message: 'End date is required'
  }
}

// 🔄 Promotion Model Utilities
export class PromotionModel {
  
  // Create a new promotion instance
  static create(data = {}) {
    return {
      ...PromotionSchema,
      ...data,
      _id: data._id || '',
      isActive: data.isActive !== false
    }
  }

  // Validate promotion data
  static validate(promotionData) {
    const errors = {}
    
    // Validate name
    if (!promotionData.name || !promotionData.name.trim()) {
      errors.name = 'Promotion name is required'
    } else if (promotionData.name.trim().length < VALIDATION_RULES.name.minLength) {
      errors.name = `Name must be at least ${VALIDATION_RULES.name.minLength} characters`
    } else if (promotionData.name.trim().length > VALIDATION_RULES.name.maxLength) {
      errors.name = `Name cannot exceed ${VALIDATION_RULES.name.maxLength} characters`
    } else if (!VALIDATION_RULES.name.pattern.test(promotionData.name.trim())) {
      errors.name = VALIDATION_RULES.name.message
    }
    
    // Validate promotion type
    if (!promotionData.promotionType) {
      errors.promotionType = 'Promotion type is required'
    } else if (!Object.values(PROMOTION_TYPES).includes(promotionData.promotionType)) {
      errors.promotionType = 'Invalid promotion type'
    }
    
    // Validate discount type
    if (!promotionData.discountType) {
      errors.discountType = 'Discount type is required'
    } else if (!Object.values(DISCOUNT_TYPES).includes(promotionData.discountType)) {
      errors.discountType = 'Invalid discount type'
    }
    
    // Validate discount value
    if (!promotionData.discountValue && promotionData.discountValue !== 0) {
      errors.discountValue = 'Discount value is required'
    } else {
      const value = parseFloat(promotionData.discountValue)
      if (isNaN(value) || value <= 0) {
        errors.discountValue = 'Discount value must be greater than 0'
      } else if (value > VALIDATION_RULES.discountValue.max) {
        errors.discountValue = `Discount value cannot exceed ${VALIDATION_RULES.discountValue.max}`
      } else if (promotionData.discountType === DISCOUNT_TYPES.PERCENTAGE && value > 100) {
        errors.discountValue = 'Percentage discount cannot exceed 100%'
      }
    }
    
    // Validate priority
    if (!promotionData.priority) {
      errors.priority = 'Priority is required'
    } else if (!Object.values(PRIORITY_LEVELS).includes(parseInt(promotionData.priority))) {
      errors.priority = 'Invalid priority level'
    }
    
    // Validate target audience
    if (!promotionData.targetAudience) {
      errors.targetAudience = 'Target audience is required'
    }
    
    // Validate dates
    if (!promotionData.startDate) {
      errors.startDate = 'Start date is required'
    }
    
    if (!promotionData.endDate) {
      errors.endDate = 'End date is required'
    }
    
    if (promotionData.startDate && promotionData.endDate) {
      const startDate = new Date(promotionData.startDate)
      const endDate = new Date(promotionData.endDate)
      
      if (isNaN(startDate.getTime())) {
        errors.startDate = 'Invalid start date'
      }
      
      if (isNaN(endDate.getTime())) {
        errors.endDate = 'Invalid end date'
      }
      
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && endDate <= startDate) {
        errors.endDate = 'End date must be after start date'
      }
    }
    
    // Validate products for product-based promotions
    if (promotionData.promotionType === PROMOTION_TYPES.PRODUCT_BASED) {
      if (!promotionData.products || !Array.isArray(promotionData.products) || promotionData.products.length === 0) {
        errors.products = 'At least one product is required for product-based promotions'
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  // Validate partial data (for updates)
  static validatePartial(promotionData) {
    // Only validate fields that are provided
    const errors = {}
    
    if (promotionData.name !== undefined) {
      if (!promotionData.name.trim()) {
        errors.name = 'Promotion name cannot be empty'
      } else if (promotionData.name.trim().length < VALIDATION_RULES.name.minLength) {
        errors.name = `Name must be at least ${VALIDATION_RULES.name.minLength} characters`
      }
    }
    
    if (promotionData.discountValue !== undefined) {
      const value = parseFloat(promotionData.discountValue)
      if (isNaN(value) || value <= 0) {
        errors.discountValue = 'Discount value must be greater than 0'
      }
    }
    
    // Add other partial validations as needed
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  // Format promotion for display
  static formatForDisplay(promotion) {
    if (!promotion) return null
    
    return {
      ...promotion,
      formattedName: this.formatName(promotion),
      formattedType: this.getTypeLabel(promotion.promotionType),
      formattedDiscount: this.formatDiscountValue(promotion.discountValue, promotion.discountType),
      formattedPriority: this.getPriorityLabel(promotion.priority),
      formattedDates: this.formatDateRange(promotion.startDate, promotion.endDate),
      formattedStartDate: this.formatDate(promotion.startDate),
      formattedEndDate: this.formatDate(promotion.endDate),
      formattedCreatedAt: this.formatDate(promotion.createdAt),
      formattedUpdatedAt: this.formatDate(promotion.updatedAt),
      statusBadgeClasses: this.getStatusBadgeClasses(promotion.isActive),
      typeBadgeClasses: this.getTypeBadgeClasses(promotion.promotionType),
      priorityBadgeClasses: this.getPriorityBadgeClasses(promotion.priority),
      isCurrentlyActive: this.isCurrentlyActive(promotion),
      daysRemaining: this.getDaysRemaining(promotion.endDate),
      targetAudienceName: this.getTargetAudienceName(promotion.targetAudience),
      productsCount: Array.isArray(promotion.products) ? promotion.products.length : 0,
      conditionsCount: Array.isArray(promotion.conditions) ? promotion.conditions.length : 0
    }
  }

  // Format name with fallback
  static formatName(promotion) {
    return promotion?.name || 'Unnamed Promotion'
  }

  // Get type label
  static getTypeLabel(type) {
    const config = PROMOTION_TYPE_CONFIG[type]
    return config ? config.label : type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'
  }

  // Get priority label
  static getPriorityLabel(priority) {
    const config = PRIORITY_CONFIG[priority]
    return config ? config.label : 'Unknown'
  }

  // Format discount value
  static formatDiscountValue(value, type) {
    if (value === undefined || value === null) return 'N/A'
    
    try {
      if (type === DISCOUNT_TYPES.PERCENTAGE) {
        return `${parseFloat(value).toFixed(1)}%`
      } else if (type === DISCOUNT_TYPES.FIXED) {
        return `LKR ${parseFloat(value).toFixed(2)}`
      }
      return value.toString()
    } catch (error) {
      return 'N/A'
    }
  }

  // Format date
  static formatDate(dateString) {
    if (!dateString) return 'N/A'
    
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid Date'
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return 'Invalid Date'
    }
  }

  // Format date range
  static formatDateRange(startDate, endDate) {
    const start = this.formatDate(startDate)
    const end = this.formatDate(endDate)
    return `${start} - ${end}`
  }

  // Get status badge classes
  static getStatusBadgeClasses(isActive) {
    return isActive 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200'
  }

  // Get type badge classes
  static getTypeBadgeClasses(type) {
    const config = PROMOTION_TYPE_CONFIG[type]
    return config ? `${config.bgColor} ${config.textColor}` : 'bg-gray-100 text-gray-800'
  }

  // Get priority badge classes
  static getPriorityBadgeClasses(priority) {
    const config = PRIORITY_CONFIG[priority]
    return config ? `${config.bgColor} ${config.textColor}` : 'bg-gray-100 text-gray-800'
  }

  // Get type icon
  static getTypeIcon(type) {
    const config = PROMOTION_TYPE_CONFIG[type]
    return config ? config.icon : '🎉'
  }

  // Check if promotion is currently active
  static isCurrentlyActive(promotion) {
    if (!promotion || !promotion.isActive) return false
    
    const now = new Date()
    const startDate = new Date(promotion.startDate)
    const endDate = new Date(promotion.endDate)
    
    return now >= startDate && now <= endDate
  }

  // Get days remaining
  static getDaysRemaining(endDate) {
    if (!endDate) return null
    
    const now = new Date()
    const end = new Date(endDate)
    const diffTime = end - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    return diffDays > 0 ? diffDays : 0
  }

  // Get target audience name
  static getTargetAudienceName(targetAudience) {
    if (!targetAudience) return 'N/A'
    
    if (typeof targetAudience === 'object' && targetAudience.name) {
      return targetAudience.name
    }
    
    return 'Customer Group'
  }

  // Transform promotion data for API
  static transformForAPI(promotionData) {
    return {
      name: promotionData.name?.trim(),
      description: promotionData.description?.trim() || '',
      promotionType: promotionData.promotionType,
      discountType: promotionData.discountType,
      discountValue: parseFloat(promotionData.discountValue),
      priority: parseInt(promotionData.priority),
      targetAudience: promotionData.targetAudience,
      startDate: promotionData.startDate ? new Date(promotionData.startDate).toISOString() : null,
      endDate: promotionData.endDate ? new Date(promotionData.endDate).toISOString() : null,
      products: Array.isArray(promotionData.products) ? promotionData.products.filter(Boolean) : [],
      conditions: Array.isArray(promotionData.conditions) ? promotionData.conditions.filter(Boolean) : [],
      isActive: promotionData.isActive !== false
    }
  }

  // Search promotions
  static searchPromotions(promotions, searchTerm) {
    if (!searchTerm || !Array.isArray(promotions)) return promotions
    
    const lowercaseSearch = searchTerm.toLowerCase()
    return promotions.filter(promotion => {
      const name = promotion.name?.toLowerCase() || ''
      const description = promotion.description?.toLowerCase() || ''
      const type = this.getTypeLabel(promotion.promotionType).toLowerCase()
      
      return name.includes(lowercaseSearch) || 
             description.includes(lowercaseSearch) || 
             type.includes(lowercaseSearch)
    })
  }

  // Filter promotions by type
  static filterByType(promotions, type) {
    if (!type || !Array.isArray(promotions)) return promotions
    return promotions.filter(promotion => promotion.promotionType === type)
  }

  // Filter promotions by status
  static filterByStatus(promotions, isActive) {
    if (typeof isActive !== 'boolean' || !Array.isArray(promotions)) return promotions
    return promotions.filter(promotion => promotion.isActive === isActive)
  }

  // Filter promotions by date range
  static filterByDateRange(promotions, startDate, endDate) {
    if (!startDate || !endDate || !Array.isArray(promotions)) return promotions
    
    const start = new Date(startDate)
    const end = new Date(endDate)
    
    return promotions.filter(promotion => {
      const promoStart = new Date(promotion.startDate)
      const promoEnd = new Date(promotion.endDate)
      
      return promoStart <= end && promoEnd >= start
    })
  }

  // Sort promotions
  static sortPromotions(promotions, sortBy = 'name', sortOrder = 'asc') {
    if (!Array.isArray(promotions)) return []
    
    return [...promotions].sort((a, b) => {
      let aValue, bValue
      
      switch (sortBy) {
        case 'name':
          aValue = a.name?.toLowerCase() || ''
          bValue = b.name?.toLowerCase() || ''
          break
        case 'type':
          aValue = a.promotionType || ''
          bValue = b.promotionType || ''
          break
        case 'priority':
          aValue = a.priority || 0
          bValue = b.priority || 0
          break
        case 'startDate':
          aValue = new Date(a.startDate || 0)
          bValue = new Date(b.startDate || 0)
          break
        case 'endDate':
          aValue = new Date(a.endDate || 0)
          bValue = new Date(b.endDate || 0)
          break
        case 'createdAt':
          aValue = new Date(a.createdAt || 0)
          bValue = new Date(b.createdAt || 0)
          break
        default:
          aValue = a[sortBy] || ''
          bValue = b[sortBy] || ''
      }
      
      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      }
    })
  }

  // Calculate promotion statistics
  static calculateStats(promotions) {
    if (!Array.isArray(promotions)) {
      return {
        total: 0,
        active: 0,
        inactive: 0,
        byType: {},
        byPriority: {},
        currentlyRunning: 0
      }
    }
    
    const stats = {
      total: promotions.length,
      active: 0,
      inactive: 0,
      byType: {},
      byPriority: {},
      currentlyRunning: 0
    }
    
    promotions.forEach(promotion => {
      // Status counts
      if (promotion.isActive) {
        stats.active++
      } else {
        stats.inactive++
      }
      
      // Type counts
      const type = promotion.promotionType
      stats.byType[type] = (stats.byType[type] || 0) + 1
      
      // Priority counts
      const priority = promotion.priority
      stats.byPriority[priority] = (stats.byPriority[priority] || 0) + 1
      
      // Currently running
      if (this.isCurrentlyActive(promotion)) {
        stats.currentlyRunning++
      }
    })
    
    return stats
  }

  // Get available promotion types for dropdown
  static getPromotionTypes() {
    return Object.values(PROMOTION_TYPE_CONFIG).map(config => ({
      value: config.value,
      label: config.label,
      icon: config.icon,
      description: config.description
    }))
  }

  // Get available discount types for dropdown
  static getDiscountTypes() {
    return [
      { value: DISCOUNT_TYPES.FIXED, label: 'Fixed Amount ($)', icon: '💵' },
      { value: DISCOUNT_TYPES.PERCENTAGE, label: 'Percentage (%)', icon: '📊' }
    ]
  }

  // Get available priorities for dropdown
  static getPriorities() {
    return Object.values(PRIORITY_CONFIG).map(config => ({
      value: config.value,
      label: config.label
    }))
  }
}

export default PromotionModel